import { AddShoppingCart, Edit, LocalShipping, Mail, Repeat } from "@mui/icons-material";
import { Button, Typography } from "@mui/material";
import { UserTypeId, useAccountStore } from "../../common/account";
import { useDialog, useSimpleDialog, yesNoButtons } from "../../common/dialog";
import { UseEntityStore } from "../../common/entityStore";
import { UseListStore, createListStore } from "../../common/listStore";
import { listHeaderItems, renderListStore } from "../../common/listStoreUi";
import { MailOrderDialog } from "./MailOrderDialog";
import { OrderCycleListDialog } from "./OrderCycleListDialog";
import { CustomerDto, MailOrderDto, OrderListItem, OrderRetentionSaveDto, RetentionSaveDto } from "./types";
import { ContinuousOrderDialog } from "./ContinuousOrderDialog";
import { OrderShipmentsDialog } from "./OrderShipmentsDialog";
import { OrderStateId, RetentionCodeId } from "../../common/enum";
import { useState } from "react";
import { useStateWithDeps } from "use-state-with-deps";
import { createAxios } from "../../common/axios";
import { formatNumber } from "../../common/localization";
import { useDefaultValues } from "../../common/defaultValues";

export function OrderList(props: {
    useCustomerStore: UseEntityStore<CustomerDto>;
    useOrderListStore: UseListStore<OrderListItem>;
    onRetentionSelected(retention: OrderRetentionSaveDto): Promise<boolean>;
}) {
    const customerStore = props.useCustomerStore(p => ({
        serverData$: p.serverData$,
        id: p.serverData.id!, custId: p.serverData.custId,
        isNew: p.serverData.id == null,
    }));
    const accountStore = useAccountStore(p => ({ userTypeId: p.data?.accessTokenDecoded.userTypeId }));
    const canManage = accountStore.userTypeId !== UserTypeId.View && accountStore.userTypeId !== UserTypeId.CollectionAgency;
    const selectedOrder = props.useOrderListStore(p => p.selectedItem);
    const def = useDefaultValues(p => p.clientData);

    const [retention, setRetention] = useState<RetentionSaveDto>();
    async function onRetentionSelected(retention: RetentionSaveDto) {
        if ([RetentionCodeId.FreePack, RetentionCodeId.SkipEachSecondShp, RetentionCodeId.SkipCycle, RetentionCodeId.DelayedCancelation].includes(retention.retentionCodeId)) {
            setRetention(retention);
            orderCyclesDialog.show(() => {
                setRetention(undefined);
            });
            return true;
        }
        if ([RetentionCodeId.MailOrder].includes(retention.retentionCodeId)) {
            setRetention(retention);
            newMoDialog.show(() => {
                setRetention(undefined);
            });
            return true;
        }
        if ([RetentionCodeId.NewContOrder].includes(retention.retentionCodeId)) {
            setRetention(retention);
            newContDialog.show(() => {
                setRetention(undefined);
            });
            return true;
        }
        if ([RetentionCodeId.DueDateChange].includes(retention.retentionCodeId)) {
            setRetention(retention);
            orderShipmentsDialog.show(() => {
                setRetention(undefined);
            });
            return true;
        }
        return await props.onRetentionSelected({ ...retention, orderId: selectedOrder!.id });
    }

    const [newMoData, setNewMoData] = useState<MailOrderDto>();
    const orderShipmentsDialog = useDialog(close => <OrderShipmentsDialog close={close}
        orderId={selectedOrder!.id} ordId={selectedOrder!.ordId} useCustomerStore={props.useCustomerStore}
        retention={retention} clearRetention={() => setRetention(undefined)} />, "xl");
    const newMoDialog = useDialog(close => <MailOrderDialog close={close} orderId={null} useCustomerStore={props.useCustomerStore}
        retention={retention} clearRetention={() => setRetention(undefined)} newMoData={newMoData} />, "lg", {
        onClose() {
            setNewMoData(undefined);
        }
    });
    const newContDialog = useDialog(close => <ContinuousOrderDialog close={close} orderId={null} useCustomerStore={props.useCustomerStore}
        onRetentionSelected={onRetentionSelected} retention={retention} clearRetention={() => setRetention(undefined)} />, "sm");
    const orderCyclesDialog = useDialog(close => <OrderCycleListDialog close={close} ordId={selectedOrder!.ordId} orderId={selectedOrder!.id}
        useCustomerStore={props.useCustomerStore} retention={retention} clearRetention={() => setRetention(undefined)} />, "lg");
    const editSelectedOrderDialog = useDialog(close => selectedOrder!.ordTyp === "C" ?
        <ContinuousOrderDialog close={close} orderId={selectedOrder!.id} useCustomerStore={props.useCustomerStore}
            onRetentionSelected={onRetentionSelected} /> :
        <MailOrderDialog close={close} orderId={selectedOrder!.id} useCustomerStore={props.useCustomerStore}
            setNewMoData={p => { setNewMoData(p); newMoDialog.show(); }} />,
        selectedOrder?.ordTyp === "C" ? "sm" : "lg");

    const simpleDialog = useSimpleDialog();

    const [useSpecialActionTemplateListStore] = useStateWithDeps(() => createListStore<SpecialActionTemplateDto>({
        path: canManage ? "customers/specialActionTemplates" : undefined
    }), [canManage]);
    const specialActionTemplates = useSpecialActionTemplateListStore(p => p.items);
    function specialActionOrderButton(template: SpecialActionTemplateDto | null | undefined, templateNumber: number) {
        const templateName = template == null ? undefined : `${template.dsc} (${formatNumber(template.btoAmt, def.landCode, true)})`;
        async function addSpecialActionOrder() {
            simpleDialog.show("Confirm action", `Add new MO ${templateName} for this customer?`, undefined, yesNoButtons(async () => {
                try {
                    await createAxios().post("customers/specialActionOrders", { templateId: template!.id, customerId: customerStore.id });
                } finally {
                    props.useCustomerStore.getState().fetchServerData();
                }
            }));
        }
        return () => <Button title={templateName}
            disabled={template == null} onClick={addSpecialActionOrder}>A{templateNumber}</Button>
    }

    return <>
        {simpleDialog.render()}
        {orderShipmentsDialog.render()}
        {newMoDialog.render()}
        {newContDialog.render()}
        {orderCyclesDialog.render()}
        {editSelectedOrderDialog.render()}
        <Typography variant="h6">Orders</Typography>
        {renderListStore(props.useOrderListStore, {
            header: listHeaderItems([
                ["edit", () => <Button color="edit" disabled={customerStore.isNew || !selectedOrder} title="Edit order" onClick={() => editSelectedOrderDialog.show()}><Edit /></Button>],
                ["addMailOrder", () => <Button color="add" disabled={customerStore.isNew || !canManage} title="Add mail order" onClick={() => newMoDialog.show()}><Mail /></Button>],
                ["addContOrder", () => <Button color="add" disabled={customerStore.isNew || !canManage} title="Add continuity order" onClick={() => newContDialog.show()}><AddShoppingCart /></Button>],
                ["shipments", () => <Button disabled={customerStore.isNew || !selectedOrder}
                    title="Shipments" onClick={() => orderShipmentsDialog.show()}><LocalShipping /></Button>],
                ["orderCycles", () => <Button disabled={customerStore.isNew || selectedOrder?.ordTyp !== "C"}
                    title="Order cycles" onClick={() => orderCyclesDialog.show()}><Repeat /></Button>],
            ], [
                canManage && !customerStore.isNew && ["specialActionTemplate1", specialActionOrderButton(specialActionTemplates.at(0), 1)],
                canManage && !customerStore.isNew && ["specialActionTemplate2", specialActionOrderButton(specialActionTemplates.at(1), 2)],
                canManage && !customerStore.isNew && ["specialActionTemplate3", specialActionOrderButton(specialActionTemplates.at(2), 3)],
            ]),
            onDoubleClick: () => editSelectedOrderDialog.show(),
            getRowClass: p => (([OrderStateId.Active, OrderStateId.NoShipment].includes(p.orderStateId) ||
                (p.orderStateId === OrderStateId.DelStopped && p.act)) && ["bold", "greenRibbon"]),
        })}
    </>;
}

interface SpecialActionTemplateDto {
    id: number;
    dsc: string;
    btoAmt: number;
}
